import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { NavLink, Outlet, Link, Navigate, useLocation } from "react-router-dom";
import { Sun, Orbit, MessageCircle, BarChart3, User } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/AppShell";
import { Loader } from "@/components/Protected";

const TABS = [
  { to: "/today", label: "Today", icon: Sun, id: "today" },
  { to: "/progress", label: "Progress", icon: BarChart3, id: "progress" },
  { to: "/journey", label: "Journey", icon: Orbit, id: "journey" },
  { to: "/talk", label: "Chat", icon: MessageCircle, id: "talk" },
];

const Ctx = createContext(null);
export const useJourney = () => useContext(Ctx);

const WIDE = ["/today", "/journey", "/progress"];

export function V2Shell() {
  const [s, setS] = useState(null);
  const { pathname } = useLocation();
  const wide = WIDE.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  const reload = useCallback(() => api.get("/v2/journey").then(({ data }) => { setS(data); return data; }), []);
  useEffect(() => { reload(); }, [reload]);
  if (s === null) return <Loader />;
  if (!s.journey) return <Navigate to="/start" replace />;
  const color = s.phase.color;

  return (
    <Ctx.Provider value={{ s, setS, reload }}>
      <div className="v2 grain min-h-screen relative" style={{ "--phase": color }}>
        <header className="sticky top-0 z-30 bg-[#FCFCFA]/90 backdrop-blur-md border-b border-[#E4E1E5] pt-[env(safe-area-inset-top)]">
          <div className="max-w-[1100px] mx-auto px-6 h-14 flex items-center justify-between gap-3">
            <Link to="/today" data-testid="v2-header-logo" className="flex items-center gap-2.5"><Logo /></Link>
            <nav className="hidden lg:flex items-center gap-1">
              {TABS.map((t) => (
                <NavLink key={t.id} to={t.to} data-testid={`v2-desktop-nav-${t.id}`}
                  className={({ isActive }) => `h-11 inline-flex items-center px-4 rounded-full text-sm font-medium chip ${isActive ? "bg-[#F0EBF2] text-[#392B43] font-semibold" : "text-[#6A646D] hover:bg-[#F0EBF2]/60"}`}>{t.label}</NavLink>
              ))}
            </nav>
            <div className="flex items-center gap-2">
              <Link to="/journey" data-testid="v2-header-phase" className="hidden sm:inline-flex text-xs font-semibold px-3 py-1 rounded-full text-white chip hover:opacity-90" style={{ background: color }}>
                {s.phase.letter} · Week {s.week} · {s.phase.name}
              </Link>
              <NavLink to="/you" data-testid="v2-header-you" aria-label="You and settings"
                className={({ isActive }) => `h-11 w-11 grid place-items-center rounded-full chip ${isActive || pathname.startsWith("/you") ? "bg-[#F0EBF2] text-[#392B43]" : "text-[#6A646D] hover:bg-[#F0EBF2]/60"}`}><User className="h-5 w-5" /></NavLink>
            </div>
          </div>
        </header>
        <main className={`relative z-10 mx-auto px-6 pt-6 pb-28 lg:pb-16 ${wide ? "max-w-[1100px]" : "max-w-2xl lg:max-w-3xl"}`} data-testid="v2-main"><Outlet /></main>
        <nav className="mobile-tabbar lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#FCFCFA]/95 backdrop-blur-md border-t border-[#E4E1E5] pb-[env(safe-area-inset-bottom)]">
          <div className="flex justify-around items-center h-16">
            {TABS.map((t) => (
              <NavLink key={t.id} to={t.to} data-testid={`v2-nav-${t.id}`}
                className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 min-w-[72px] min-h-[52px] text-[11px] chip ${isActive ? "text-[#392B43] font-semibold" : "text-[#6A646D] font-medium"}`}>
                {({ isActive }) => (<><span className={`h-7 w-12 grid place-items-center rounded-full ${isActive ? "bg-[#F0EBF2]" : ""}`}><t.icon className="h-5 w-5" strokeWidth={isActive ? 2 : 1.75} /></span>{t.label}</>)}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </Ctx.Provider>
  );
}
